import { useAccountStore } from '@components/Stores/accountStore';
import { IBalanceFlowProps, IBalanceInstruction } from '@sp-types/balanceFlowType';
import ButtonDesigned from '@UI-Elements/Buttons/ButtonDesigned';
import React, { useEffect, useState } from 'react';
import { useDemoModeStore } from '../../../../../components/Stores/demoModeStore';
import NewDocUploadPanel from '../../../../../components/UI-Elements/FileUpload/DocUploadPanel/NewDocUploadPanel';
import { addNotification } from '../../../../AdminChanges/AdminUtils';
import { LoadingLogoComponent } from '../../../../Quotes/LoadingLogoComponent';
import { mapBalanceInstToPosition, updateBalanceStepsStatusByIndexes } from '../../../balanceUtils';
import { ExecutionDealCharacteristics } from '../components/BFDealCharacteristics';
import { ExecutionTable } from '../components/ExecutionTable';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { savePosition } from '@sp-firebase/financialPositionsFB';
import { debounce } from 'lodash';
import { useAdminModeStore } from '@components/Stores/EditAdminStore';
import { Timestamp } from 'firebase/firestore';
import { Button } from '@mui/material';
import { triggerExposureCalc } from '../../../../../hooks/useExposure';
import { FileUploadedSuccess } from '../../../../../components/UI-Elements/FileUpload/FileUploadedSuccess';

export const BalanceFlowExecutionReport: React.FC<IBalanceFlowProps> = ({
  balanceFlowData,
  currentStepIndex,
  setCurrentStepIndex,
  updateBalanceFlowData,
}) => {
  const { balanceInstructions, executionDocPath } = balanceFlowData;
  const [isPendingOverlay, setIsPendingOverlay] = useState(false);
  const [isAnalyzingDoc, setIsAnalyzingDoc] = useState(false);
  const { account } = useAccountStore();
  const { isDemoMode } = useDemoModeStore();

  const { isAdmin } = useAdminModeStore();

  const { materialsMap } = useMaterialsStore();

  const [balanceInstructionData, setBalanceInstructionsData] = useState<IBalanceInstruction[]>(
    balanceInstructions || [],
  );

  const [isSaveButtonLoading, setIsSaveButtonLoading] = useState(false);
  const makeEditable = (instructions: IBalanceInstruction[]): IBalanceInstruction[] =>
    instructions.map(({ status, ...i }) => ({
      status: isDemoMode || !isAdmin ? status : i.executedNumberOfContracts && i.executedPrice ? 'executed' : 'edit',
      ...i,
    }));

  useEffect(() => {
    balanceFlowData.balanceInstructions &&
      setBalanceInstructionsData(makeEditable(balanceFlowData.balanceInstructions));
  }, [balanceFlowData]);

  const updateInstruction = (
    instruct: IBalanceInstruction,
    contractCount?: number,
    price?: number,
  ): IBalanceInstruction => {
    if (price || price === 0) {
      instruct.executedPrice = price;
    }
    if (contractCount || contractCount === 0) {
      instruct.executedNumberOfContracts = contractCount;
    }

    instruct.status = instruct.executedNumberOfContracts && instruct.executedPrice ? 'executed' : 'edit';

    return instruct;
  };

  const onUpdateExecution = (instrument: string, contractCount?: number, price?: number) => {
    setBalanceInstructionsData((instructions) => {
      const updatedInstructions = instructions.map((i) =>
        i.instrumentId === instrument ? updateInstruction(i, contractCount, price) : i,
      );

      debouncedUpdateBalanceFlow(updatedInstructions);

      return updatedInstructions;
    });
  };

  const debouncedUpdateBalanceFlow = React.useCallback(
    debounce(async (balanceInstructions: IBalanceInstruction[]) => {
      await updateBalanceFlowData({ ...balanceFlowData, balanceInstructions });
    }, 1500),
    [],
  );

  const adminApproveExecution = async () => {
    await updateBalanceFlowData({ ...balanceFlowData, status: 'underwriting' });
  };

  async function savingAction() {
    setIsSaveButtonLoading(true);

    if (!balanceFlowData.savedPositions) {
      // in future - we should use Mutex
      balanceFlowData.savedPositions = true;
      const financialPositions = mapBalanceInstToPosition(balanceFlowData.balanceInstructions || [], balanceFlowData);

      await Promise.all(financialPositions.map((position, i) => savePosition(position, i)));
    }
    const results = await Promise.all([
      addNotification(account.id || '', {
        id: 'not_info_targetprice_' + Timestamp.now().seconds,
        date: Timestamp.now(),
        type: 'info',
        text: (materialsMap[balanceFlowData.materialId]?.name || '') + ' price is at target',
        descriptionText: 'Click here to view',
        materialId: [balanceFlowData.materialId],
        status: 'completed',
      }),
      updateBalanceFlowData({
        ...balanceFlowData,
        savedPositions: true,
        steps: updateBalanceStepsStatusByIndexes(balanceFlowData.steps, [
          { index: currentStepIndex, status: 'Approved' },
          { index: currentStepIndex + 1, status: 'Incomplete' },
        ]),
      }),
      triggerExposureCalc({ accountId: account.id || '', materialIds: [balanceFlowData.materialId] }),
    ]);

    console.log('Refreshed', results?.[2]);

    setCurrentStepIndex((prevIndex: number) => prevIndex + 1);
    setIsSaveButtonLoading(false);
  }

  // Get Out
  async function updateBalanceFlowInstructionsWithMockData() {
    updateBalanceFlowData({
      ...balanceFlowData,
      balanceInstructions: updateBalanceFlowExecutionInstructions(balanceInstructionData),
    });
  }

  // Get Out
  const onUploadDocRegular = async (path: string) => {
    await updateBalanceFlowData({
      ...balanceFlowData,
      steps: updateBalanceStepsStatusByIndexes(balanceFlowData.steps, [
        { index: currentStepIndex, status: path ? 'Pending' : 'Incomplete' },
      ]),
      executionDocPath: path,
      status: path ? 'pending' : 'underwriting',
    });
    path && setIsPendingOverlay(true);
  };

  // Get Out
  const onUploadDocDemoMode = async (path: string) => {
    await updateBalanceFlowData({
      ...balanceFlowData,
      steps: updateBalanceStepsStatusByIndexes(balanceFlowData.steps, [
        { index: currentStepIndex, status: 'Incomplete' },
      ]),
      executionDocPath: path,
      balanceInstructions: updateBalanceFlowExecutionInstructions(balanceInstructionData),
    });
  };

  // Get Out
  const onUploadDoc1 = async (path: string) => {
    setIsAnalyzingDoc(true);
    isDemoMode ? await onUploadDocDemoMode(path) : await onUploadDocRegular(path);
    setIsAnalyzingDoc(false);
  };

  return (
    <>
      <div className="flex  flex-col flex-1 space-y-4 overflow-y-auto ">
        <div className="space-y-4">
          <div className="">
            <h2 className="text-lg">Execution report</h2>
          </div>
          <div className="flex flex-1  max-w-[40rem]">
            <ExecutionDealCharacteristics balanceInstructions={balanceInstructionData} />
          </div>
          <div className="border"></div>
          <div className="flex justify-between">
            <h6 className="text-red  ">Executed protections</h6>
            {isDemoMode && (
              <ButtonDesigned style="alert" onButtonClick={() => updateBalanceFlowInstructionsWithMockData()}>
                Demo : Complete execution
              </ButtonDesigned>
            )}
          </div>
          <div className=" flex max-h-[20rem] border border-gray-150 ">
            {balanceInstructions && (
              <ExecutionTable tableData={balanceInstructionData} onUpdateExecution={onUpdateExecution} isShowExecuted />
            )}
          </div>
          {!balanceInstructionData.some((element) => element.status !== 'executed') && (
            <div className="flex text-green-300 gap-2">
              <img src={'../assets/icons/colored/Approved_status.svg'}></img>
              All transactions completed
              {isAdmin && balanceFlowData.status === 'pending' && (
                <Button onClick={adminApproveExecution}>admin: Set balance underwriting</Button>
              )}
            </div>
          )}
        </div>
        <div className="my-auto">
          <h2 className="text-base">Execution report</h2>
          <div className="flex space-x-4">
            {/* <DocUploadPanel
            onDeleteDocument={onDeleteDoc}
            onUploadNewDocument={onUploadDoc}
            uploadedDocumentPath={executionDocPath ? executionDocPath : ''}
          /> */}
            <NewDocUploadPanel
              setUploadedDocumentPath={onUploadDoc1}
              bucketSavingLocation={`balance/${account.id}/${balanceFlowData.id}/execution-parameters/`}
              uploadedDocumentPath={executionDocPath}
            />
            {isAnalyzingDoc && (
              <div className="my-auto">
                <LoadingLogoComponent size={40} />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-row mt-auto space-x-4 ">
          <div>
            <ButtonDesigned
              backButton
              className="py-2"
              style="outline"
              onButtonClick={() => setCurrentStepIndex((currIndex) => currIndex - 1)}
            ></ButtonDesigned>
          </div>
          <ButtonDesigned
            disableButton={
              balanceInstructionData.some((element) => element.status !== 'executed') || isSaveButtonLoading
            }
            onButtonClick={() => savingAction()}
            isFunctionLoading={isSaveButtonLoading}
            className="py-2 px-4"
          >
            Save
          </ButtonDesigned>
        </div>
      </div>
      {isPendingOverlay && <FileUploadedSuccess close={() => setIsPendingOverlay(false)} />}
    </>
  );
};

const updateBalanceFlowExecutionInstructions = (originalData: IBalanceInstruction[]): IBalanceInstruction[] => {
  return originalData.map((instruction) => ({
    ...instruction,
    status: 'executed',
    executedPrice: Math.floor(instruction.maximumPrice * 0.98),
    executedNumberOfContracts: instruction.numberOfContracts,
  }));
};
